// Tempo- und Taktart-Bearbeitung auf dem Projekt (innerhalb von commit()). MIDI, Marker, Akkorde und Loop bleiben auf ihren Takten.
import type { Project } from '@srs/shared';
import { ensureWarp, isElastic } from './warp.ts';
import { beatAt, barAtSec, removeMeter, removeTempo, retime, setMeter, setTempo, tempoEvents, tempoSnapshot } from '@srs/shared';

const withRetime = (p: Project, fn: () => void, mode: 'bars' | 'time' = 'bars') => {
  // Audio auf Elastic-Spuren wird tick-basiert: Warp so anlegen, wie es jetzt klingt, dann folgt es den Takten
  if (mode === 'bars') for (const c of p.clips) if (c.kind === 'audio' && !c.warp && isElastic(p.tracks.find((t) => t.id === c.trackId))) ensureWarp(p, c);
  const before = tempoSnapshot(p); fn(); retime(p, before, mode);
};

/** Tempo des Abschnitts ändern, in dem sec liegt (Grundtempo oder der letzte Wechsel davor) */
export function changeTempoAt(p: Project, sec: number, bpm: number) {
  const b = beatAt(p, sec), ev = tempoEvents(p).filter((e) => e.beat <= b + 1e-6).pop() ?? { beat: 0 };
  withRetime(p, () => setTempo(p, ev.beat, bpm));
}
/** Tempowechsel auf einer Viertel-Position setzen oder ändern */
export function putTempo(p: Project, beat: number, bpm: number) { withRetime(p, () => setTempo(p, Math.max(0, beat), bpm)); }
export function deleteTempo(p: Project, beat: number) { if (beat > 1e-6) withRetime(p, () => removeTempo(p, beat)); }
/** Taktart ab Takt (0-basiert) setzen */
export function putMeter(p: Project, bar: number, num: number, den: number) { withRetime(p, () => setMeter(p, Math.max(0, Math.round(bar)), num, den)); }
export function deleteMeter(p: Project, bar: number) { if (bar > 0) withRetime(p, () => removeMeter(p, bar)); }
/** Takt (0-basiert, ganzzahlig) an einer Zeit */
export const barIndexAt = (p: Project, sec: number) => Math.max(0, Math.floor(barAtSec(p, sec) + 1e-6));
/** Tempo-Spur ersetzen: Wechsel zwischen zwei Viertel-Positionen entfernen und neue setzen (Beat Detective).
 *  Alles bleibt zeitlich, wo es ist; nur das Raster folgt danach der Aufnahme. */
export function replaceTempos(p: Project, fromBeat: number, toBeat: number, events: { beat: number; bpm: number }[]) {
  withRetime(p, () => {
    p.tempos = (p.tempos ?? []).filter((e) => e.beat < fromBeat - 1e-6 || e.beat > toBeat + 1e-6);
    for (const e of events) setTempo(p, e.beat, Math.round(e.bpm * 100) / 100);
  }, 'time');
}
